import { useState } from 'react';

import { Box, Button, Icon, Input, Label } from '@betterment/samba-react';
import { safeDateString } from '@betterment/shared-components/src/components/activity/utils/dateUtils';

import { type CustomDateRange, dateRangeOptions, isCustom, isCustomDateRangeValid } from './dateUtils';

import { trackClick } from '../../../features/track';
import { DateRangeOption } from '../../../graphql/schema';

export const DateRangeFilter: React.FC<{
  onChange: (option: DateRangeOption, customDateRange?: CustomDateRange) => void;
  selected: DateRangeOption;
  customDateRange?: CustomDateRange;
}> = ({ onChange, selected, customDateRange }) => {
  const [customStartDate, setCustomStartDate] = useState<string>(customDateRange?.start ?? '');
  const [customEndDate, setCustomEndDate] = useState<string>(customDateRange?.end ?? '');
  const [isEditingCustomRange, setIsEditingCustomRange] = useState(isCustom(selected));

  const handleDateRangeOptionClick = (value: DateRangeOption) => {
    trackClick({
      name: 'timeSeriesInteraction',
      module: 'Performance',
      location: 'DateRangeFilter',
    });

    if (isCustom(value)) {
      setIsEditingCustomRange(true);

      // We only want to emit the custom date range if it's valid.
      // If it's not valid, we'll wait for the user to input a valid range.
      if (isCustomDateRangeValid(customStartDate, customEndDate)) {
        onChange(DateRangeOption.Custom, { start: customStartDate, end: customEndDate });
      }
    } else {
      onChange(value);
    }
  };

  const handleDismissCustomRange = () => {
    setIsEditingCustomRange(false);
    setCustomStartDate('');
    setCustomEndDate('');
    onChange(DateRangeOption.AllTime);
  };

  const conditionallyEmitCustomDateOption = (start: string, end: string) => {
    if (isCustomDateRangeValid(start, end)) {
      onChange(DateRangeOption.Custom, { start, end });
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCustomStartDate(value);
    conditionallyEmitCustomDateOption(value, customEndDate);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCustomEndDate(value);
    conditionallyEmitCustomDateOption(customStartDate, value);
  };

  return (
    <Box
      role="radiogroup"
      aria-label="Date range filter"
      css={{
        display: 'flex',
        alignItems: 'center',
        minHeight: '90px', // to prevent layout shift when the calendar inputs appear
        gap: 'space.100',
        paddingY: 'space.300',
        boxShadow: 'xs',
      }}
    >
      {!isEditingCustomRange &&
        dateRangeOptions.map(option => (
          <Button
            key={option.value}
            onClick={() => handleDateRangeOptionClick(option.value)}
            variant={selected === option.value ? 'solidPrimary' : 'ghostSecondary'}
            spacing="compact"
            role="radio"
            aria-checked={selected === option.value}
            aria-label={option.description}
            css={{
              minWidth: 'size.500',
              borderRadius: '20px', // pill-shaped buttons
            }}
          >
            {option.label}
          </Button>
        ))}
      {isEditingCustomRange && (
        <Box css={{ display: 'flex' }}>
          <Button
            onClick={handleDismissCustomRange}
            variant="solidPrimary"
            spacing="compact"
            role="radio"
            aria-checked={true}
            aria-label="Custom range"
            css={{
              minWidth: 'size.500',
              borderRadius: '20px',
            }}
          >
            Custom
            <Icon name="X" size="xsmall" />
          </Button>
          <Box css={{ display: 'flex', gap: 'space.150', marginLeft: 'space.200' }}>
            <Box css={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 'space.150' }}>
              <Input
                id="fromDate"
                type="date"
                aria-label="From"
                spacing="compact"
                value={customStartDate}
                max={safeDateString(new Date()) ?? undefined}
                onChange={handleStartDateChange}
              />
            </Box>
            <Box css={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 'space.150' }}>
              <Label htmlFor="toDate" css={{ alignSelf: 'center' }}>
                To
              </Label>
              <Input
                id="toDate"
                type="date"
                aria-label="To"
                spacing="compact"
                value={customEndDate}
                max={safeDateString(new Date()) ?? undefined}
                onChange={handleEndDateChange}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
